import type { Position, GeocodeResult } from '../types';
import { handleApiError } from '../services/errorHandler';
import { ErrorCodes } from '../types/errors';

/**
 * Address Resolver Service using OpenStreetMap's Nominatim API
 * Handles reverse geocoding: coordinates → structured address data
 * Optimized for route points and location context with caching and rate limiting
 */

// Cache für Geocoding-Ergebnisse um API-Calls zu reduzieren
const geocodingCache = new Map<string, GeocodeResult>();

// Rate limiting: max 1 request per second for Nominatim (to be respectful)
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1100; // 1.1 seconds to be safe

/**
 * Converts coordinates to a human-readable address using OpenStreetMap's Nominatim API
 * @param position The lat/lng coordinates
 * @returns Promise resolving to a structured GeocodeResult object
 */
export const reverseGeocode = async (position: Position): Promise<GeocodeResult> => {
  const cacheKey = `${position.lat.toFixed(4)},${position.lng.toFixed(4)}`;
  
  // Check cache first
  if (geocodingCache.has(cacheKey)) {
    return geocodingCache.get(cacheKey)!;
  }
  
  try {
    // Rate limiting
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;
    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      await new Promise(resolve => setTimeout(resolve, MIN_REQUEST_INTERVAL - timeSinceLastRequest));
    }
    lastRequestTime = Date.now();
    
    // Use API proxy to avoid CORS issues
    const baseUrl = import.meta.env.DEV 
      ? 'http://localhost:3333/api' 
      : '/api';
    
    const response = await fetch(
      `${baseUrl}/reverse?format=json&lat=${position.lat}&lon=${position.lng}&zoom=18&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'CyclingBuddy/1.0 (https://github.com/mkamw/CyclingBuddy)',
        },
      }
    );
    
    if (!response.ok) {
      // Handle specific HTTP error codes
      if (response.status === 503) {
        // Service Unavailable - this is our simulated geocoding service down
        const errorData = await response.json().catch(() => ({}));
        const networkError = new Error(errorData.details || 'Geocoding service (Nominatim) is not available.') as any;
        // Attach HTTP status to the error object so handleApiError can access it
        networkError.status = response.status;
        await handleApiError(networkError, 'nominatim/reverse', 'reverse_geocode', {
          position: `${position.lat.toFixed(4)},${position.lng.toFixed(4)}`,
          errorCode: ErrorCodes.NETWORK_UNAVAILABLE,
          cacheKey,
          service: 'geocoding',
          serviceType: 'Geocoding service (Nominatim)',
          customMessage: 'Geocoding service (Nominatim) is not available.'
        });
        
        // Return fallback result for service unavailable
        return {
          displayName: `${position.lat.toFixed(4)}, ${position.lng.toFixed(4)}`,
          rawData: null
        };
      }
      
      const httpError = new Error(`HTTP error! status: ${response.status}`) as any;
      httpError.status = response.status;
      throw httpError;
    }
    
    const data = await response.json();
    
    // Parse the data into structured format
    const result = parseNominatimData(data);
    
    // Cache the result
    geocodingCache.set(cacheKey, result);
    
    return result;
  } catch (error) {
    // Enhanced error detection for different scenarios
    let isNetworkError = false;
    let serviceType = 'Geocoding service (Nominatim)';
    let customMessage = '';
    
    if (error instanceof TypeError || (error as Error).message.includes('Failed to fetch')) {
      // Network connectivity error - proxy not running or unreachable
      isNetworkError = true;
      customMessage = 'Geocoding service (Nominatim) is not available.';
    } else if ((error as Error).message.includes('HTTP error! status: 503')) {
      // Service unavailable - proxy running but Nominatim down
      isNetworkError = true;
      customMessage = 'Geocoding service (Nominatim) is temporarily unavailable.';
    } else if ((error as Error).message.includes('HTTP error!')) {
      // Other HTTP errors - might be service-related
      isNetworkError = true;
      customMessage = 'Geocoding service (Nominatim) is experiencing issues.';
    }
    
    console.error('Reverse geocoding error:', error);
    
    await handleApiError(error as Error, 'nominatim/reverse', 'reverse_geocode', {
      position: `${position.lat.toFixed(4)},${position.lng.toFixed(4)}`,
      errorCode: isNetworkError ? ErrorCodes.NETWORK_UNAVAILABLE : ErrorCodes.API_SERVER_ERROR,
      cacheKey,
      service: isNetworkError ? 'geocoding' : undefined,
      serviceType: isNetworkError ? serviceType : undefined,
      customMessage: isNetworkError ? customMessage : undefined
    });
    
    // Return fallback result
    return {
      displayName: `${position.lat.toFixed(4)}, ${position.lng.toFixed(4)}`,
      rawData: null
    };
  }
};

/**
 * Parses the Nominatim response into a structured GeocodeResult object
 */
const parseNominatimData = (data: any): GeocodeResult => {
  if (!data) {
    return {
      displayName: 'Unbekannte Adresse',
      rawData: data
    };
  }
  
  const addr = data.address || {};
  
  // Extract structured address components
  const street = addr.road || addr.pedestrian || addr.footway || addr.cycleway;
  const houseNumber = addr.house_number;
  const city = addr.city || addr.town || addr.village;
  const suburb = addr.suburb || addr.neighbourhood || addr.quarter;
  const country = addr.country;
  
  return {
    street,
    houseNumber,
    city,
    suburb,
    country,
    displayName: data.display_name || 'Unbekannte Adresse',
    rawData: data
  };
};

/**
 * Clear the geocoding cache (useful for testing or memory management)
 */
export const clearGeocodeCache = (): void => {
  geocodingCache.clear();
};

/**
 * Get cache size for debugging
 */
export const getGeocacheCacheSize = (): number => {
  return geocodingCache.size;
};

/**
 * Formats a GeocodeResult object into a human-readable string
 * @param result The GeocodeResult to format
 * @returns A formatted address string
 */
export const formatGeocodeResult = (result: GeocodeResult): string => {
  if (!result) {
    return 'Unbekannte Adresse';
  }

  const parts: string[] = [];

  // Add street and house number
  if (result.street) {
    if (result.houseNumber) {
      parts.push(`${result.street} ${result.houseNumber}`);
    } else {
      parts.push(result.street);
    }
  }

  // Add suburb/neighbourhood if different from city
  if (result.suburb && result.suburb !== result.city) {
    parts.push(result.suburb);
  }

  // Add city
  if (result.city) {
    parts.push(result.city);
  }

  // Add country
  if (result.country) {
    parts.push(result.country);
  }

  // If we have structured parts, join them
  if (parts.length > 0) {
    return parts.join(', ');
  }

  // Fallback to display name
  return result.displayName || 'Unbekannte Adresse';
};


